/**
 * External dependencies
 */
import { v4 as uuidv4 } from 'uuid';

/**
 * Internal dependencies
 */
import type {
  Element,
  Groups,
  Page,
} from '@src/web-stories-wp/story-editor/src/elements/types';
import updateProperties from '../components/inspector/design/updateProperties';
import generateGroupName from './generateGroupName';

type AddGroup = (payload: { groupId: string; name: string }) => void;
type ArrangeElement = (payload: {
  elementId: string;
  position: number;
}) => void;
type DeleteGroupById = (payload: { groupId: string }) => void;
type UpdateElementsById = (payload: {
  elementIds: Array<Element['id']>;
  properties:
    | Partial<Element>
    | ((currentProperties: Element) => Partial<Element>);
}) => void;

type GroupSelectedElementsProps = {
  selectedElements: Element[];
  currentPage: Page | null;
  addGroup: AddGroup;
  updateElementsById: UpdateElementsById;
  arrangeElement: ArrangeElement;
};

type UngroupSelectedElementsProps = {
  selectedElements: Element[];
  deleteGroupById: DeleteGroupById;
  updateElementsById: UpdateElementsById;
};

export function groupSelectedElements({
  selectedElements: allSelectedElements,
  currentPage,
  addGroup,
  updateElementsById,
  arrangeElement,
}: GroupSelectedElementsProps): boolean {
  const selectedElements = allSelectedElements.filter(
    (element) => !('isBackground' in element) || !element.isBackground
  );

  if (!currentPage || !selectedElements || selectedElements.length === 0) {
    return false;
  }

  const { elements } = currentPage;
  const groups: Groups = currentPage.groups ?? {};

  if (!elements?.length) {
    return false;
  }

  const groupId = uuidv4();
  const name = generateGroupName(groups);

  addGroup({ groupId, name });
  updateElementsById({
    elementIds: selectedElements.map(({ id }) => id),
    properties: (currentProperties) =>
      updateProperties(
        currentProperties,
        {
          groupId,
        },
        /* commitValues */ true
      ),
  });

  const elementFromGroupWithHighestPosition = Math.max(
    ...selectedElements.map((el) =>
      elements.findIndex(({ id }) => id === el?.id)
    )
  );

  const reversedSelection = [...selectedElements].reverse();
  for (const [index, element] of Array.from(reversedSelection.entries())) {
    const position = elements.findIndex(({ id }) => id === element?.id);
    if (position !== elementFromGroupWithHighestPosition) {
      const newPosition = elementFromGroupWithHighestPosition - index;
      arrangeElement({
        elementId: element.id,
        position: newPosition,
      });
    }
  }

  return true;
}

export function ungroupSelectedElements({
  selectedElements,
  deleteGroupById,
  updateElementsById,
}: UngroupSelectedElementsProps): boolean {
  if (!selectedElements || selectedElements.length === 0) {
    return false;
  }

  const groupId = selectedElements[0]?.groupId;
  if (!groupId) {
    return false;
  }

  deleteGroupById({ groupId });

  updateElementsById({
    elementIds: selectedElements.map(({ id }) => id),
    properties: (currentProperties) =>
      updateProperties(
        currentProperties,
        {
          groupId: null,
        },
        /* commitValues */ true
      ),
  });

  return true;
}
